/**
 * Harbour Towage — the original game, behind the mode interface.
 *
 * Tow contract barges from the fairway to their berths, scored on time and
 * gentleness. All the behaviour that used to live in main.ts's boot function
 * now lives here; the core loop just calls the interface.
 */

import { Vector3 } from 'three/webgpu'
import type { Group } from 'three/webgpu'
import { Barge } from '../entities/Barge.ts'
import { Harbour, TUG_SPAWN } from '../world/Harbour.ts'
import { ContractBoard } from './Contracts.ts'
import type { GameMode, ModeContext } from './GameMode.ts'
import type { WakeSource } from '../world/Ocean.ts'

const HOOK_RANGE = 55

const _tugTow = new Vector3()
const _bargeTow = new Vector3()

export class HarbourMode implements GameMode {
  readonly worldGroup: Group
  readonly tugSpawn: { position: Vector3; heading: number }

  private readonly harbour = new Harbour()
  private readonly board = new ContractBoard()
  private barge: Barge | null = null
  private respawnTimer = 0

  constructor(private readonly ctx: ModeContext) {
    this.worldGroup = this.harbour.group
    // Ocean-going ships start outside the heads, away from the quay and cargo
    // pickup lane. The liner still fits comfortably inside the harbour.
    this.tugSpawn = ctx.tug.config.length > 300
      ? { position: new Vector3(290, 0, -570), heading: Math.PI }
      : ctx.tug.kind === 'cruise-ship'
      ? { position: new Vector3(0, 0, -50), heading: Math.PI }
      : TUG_SPAWN
  }

  start(): void {
    void this.harbour.loadModel()
    this.spawnContract()
  }

  private spawnContract(): void {
    const contract = this.board.offer()
    const spawn = this.board.spawnFor(contract)
    if (this.barge) {
      this.ctx.scene.remove(this.barge.group)
      this.barge.dispose()
    }
    this.barge = new Barge(contract.id)
    this.barge.position.copy(spawn.position)
    this.barge.yaw = spawn.heading
    this.ctx.scene.add(this.barge.group)
    this.ctx.hud.announce(`New contract: ${contract.description}`, 6)
  }

  fixedStep(dt: number, elapsed: number): void {
    const { tug } = this.ctx
    this.barge?.step(dt, elapsed)
    if (this.barge) tug.collideWithVessel(this.barge)
    tug.resolveCollisions(this.harbour.obstacles)
    this.barge?.resolveCollisions(this.harbour.obstacles)
    this.board.update(dt)
  }

  toggleHook(): void {
    const { tug, hawser, hud } = this.ctx
    if (hawser.connected) {
      hawser.release()
      if (this.barge && this.board.tryDeliver(this.barge)) {
        hud.announce(this.board.lastResult!, 6)
        this.board.lastResult = null
        this.respawnTimer = window.setTimeout(() => this.spawnContract(), 5000)
      } else {
        hud.announce('Tow released', 2)
      }
      return
    }
    if (!this.barge) return
    tug.towPointWorld(_tugTow)
    this.barge.towPointWorld(_bargeTow)
    if (_tugTow.distanceTo(_bargeTow) > HOOK_RANGE) {
      hud.announce('Too far from the barge to pass a line', 2.5)
      return
    }
    hawser.attach(
      { vessel: tug, bodyPoint: tug.attachments.towPoint },
      { vessel: this.barge, bodyPoint: this.barge.attachments.towPoint },
    )
    this.board.markTowing()
    hud.announce('Line made fast — take up gently', 3)
  }

  frame(elapsed: number): void {
    const { tug, hawser, hud, waypoint, chase } = this.ctx
    this.barge?.syncVisual()
    this.harbour.update(elapsed)

    const objective =
      this.board.current && this.barge
        ? hawser.connected
          ? this.board.current.berth.position
          : this.barge.position
        : null
    const distance = waypoint.update(tug.position, objective, chase.camera)

    if (this.barge) {
      tug.towPointWorld(_tugTow)
      this.barge.towPointWorld(_bargeTow)
      hud.setHookState(_tugTow.distanceTo(_bargeTow) <= HOOK_RANGE, hawser.connected)
    } else {
      hud.setHookState(false, false)
    }

    hud.setContract(this.board.current, this.board.earnings, distance)
  }

  towWake(): WakeSource | null {
    if (!this.barge) return null
    return {
      x: this.barge.position.x,
      z: this.barge.position.z,
      yaw: this.barge.yaw,
      speed: this.barge.speed,
      wash: 0,
      halfBeam: this.barge.config.beam * 0.5,
    }
  }

  waterExclusions() {
    return this.barge ? [this.barge.waterExclusion] : []
  }

  dispose(): void {
    clearTimeout(this.respawnTimer)
    this.ctx.scene.remove(this.worldGroup)
    this.harbour.dispose()
    if (this.barge) {
      this.ctx.scene.remove(this.barge.group)
      this.barge.dispose()
    }
  }

  get trafficObstacles() { return this.harbour.obstacles }

  /** Dev-handle accessor. */
  getBarge(): Barge | null {
    return this.barge
  }
}
